import * as React from 'react';
import { InputProps } from 'interfaces';
import { Input } from '../input/input';
import { CypressI } from 'interfaces/cypress';
import Icons from '@/const/icons';
import clsx from 'clsx';
import { DateRange } from 'react-day-picker';
import { format, parseISO } from 'date-fns';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Icon, Typography } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';

export const InputRangeDate: React.FC<
	InputProps &
		CypressI &
		React.InputHTMLAttributes<HTMLInputElement> & {
			buttonContent?: string | JSX.Element;
			buttonCustom?: boolean;
			isFormatted?: boolean;
			dateFrom?: string;
			dateTo?: string;
			restrictToPast?: boolean;
			disabled?: boolean;
		}
> = ({
	name,
	setValueInput,
	classNameInput,
	classNameTitle,
	placeholder,
	buttonCustom,
	buttonContent,
	dateFrom,
	dateTo,
	isFormatted = true,
	restrictToPast,
	disabled,
	...props
}) => {
	const popoverRef = React.useRef<HTMLDivElement | null>(null);
	const [isOpen, setIsOpen] = React.useState(false);
	const [date, setDate] = React.useState<DateRange | undefined>();
	const [initialMonth, setInitialMonth] = React.useState<Date | undefined>(
		undefined
	);

	React.useEffect(() => {
		window.__calendarRef = popoverRef;
	}, []);

	React.useEffect(() => {
		if (!isOpen) return;

		const parsedFrom = dateFrom ? new Date(`${dateFrom}T12:00:00`) : undefined;
		const parsedTo = dateTo ? new Date(`${dateTo}T12:00:00`) : undefined;

		if (parsedFrom && parsedTo) {
			setDate({ from: parsedFrom, to: parsedTo });
			setInitialMonth(parsedFrom);
		} else {
			setDate(undefined);
			setInitialMonth(new Date());
		}
	}, [isOpen, dateFrom, dateTo]);

	const clear = (): void => {
		setDate(undefined);
		setValueInput(name, undefined);
	};

	const applyChanges = (): void => {
		if (date?.from && date?.to) {
			const formattedDateFrom = format(new Date(date.from), 'yyyy-MM-dd');
			const formattedDateTo = format(new Date(date.to), 'yyyy-MM-dd');
			if (isFormatted) {
				setValueInput(
					name,
					`${format(parseISO(formattedDateFrom), 'MM/dd/yyyy')} - ${format(
						parseISO(formattedDateTo),
						'MM/dd/yyyy'
					)}`
				);
			} else {
				setValueInput(name, {
					from: formattedDateFrom,
					to: formattedDateTo,
				});
			}

			setIsOpen(false);
		}
	};

	const handleSelect = (range: DateRange | undefined): void => {
		setDate(range);
		if (range?.from) {
			setInitialMonth(range.from);
		}
	};

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger
				disabled={disabled}
				className={clsx(
					'relative p-0 disabled:cursor-not-allowed',
					{
						'p-2': buttonContent,
					},
					props.classNameTrigger
				)}
				// now onOpenChange handles the toggle
				// onClick={() => {
				// 	setIsOpen(!isOpen);
				// }}
			>
				{buttonCustom ? (
					buttonContent
				) : (
					<Input
						name={name}
						type="text"
						iconRight={Icons.calendar}
						classNameIcon={clsx('!size-5', props.classNameIcon)}
						placeholder={placeholder || 'MM/DD/YYYY - MM/DD/YYYY'}
						classNameInput={clsx(
							'!placeholder-opacity-black font-normal text-black pointer-events-none !outline-none',
							classNameInput
						)}
						classNameTitle={clsx('font-normal', classNameTitle)}
						{...props}
					/>
				)}
			</PopoverTrigger>
			<PopoverContent
				ref={popoverRef}
				className="w-auto 3xl:p-4 border-0 rounded-[20px] shadow-lg 3xl:pt-3 mb-10 3xl:space-y-3"
				align="start"
				onOpenAutoFocus={(e) => e.preventDefault()}
				// onPointerDownOutside={() => setIsOpen(false)}
			>
				<Typography type="semibold-16">Select Date Range</Typography>
				<Calendar
					showOutsideDays={false}
					key={initialMonth?.toISOString() ?? 'calendar'}
					mode="range"
					numberOfMonths={2}
					defaultMonth={initialMonth}
					selected={date}
					onSelect={handleSelect}
					iconSize="w-6 h-6"
					toDate={restrictToPast ? new Date() : undefined}
					classNames={{
						day_selected:
							'bg-purple-600 text-white hover:bg-purple-700 hover:text-white focus:bg-purple-700 focus:text-white',
						day_range_middle:
							'bg-transparent !text-slate-900 hover:!text-white',
						day_today: ` ${
							date
								? 'text-black border-none'
								: 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50'
						}`,
						day_range_start: '!bg-purple-600 text-white',
						day_range_end:
							'bg-purple-600 text-white hover:bg-purple-700 hover:text-white',
						cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-start)]:bg-purple-600 [&:has([aria-selected].day-range-start)]:text-white [&:has([aria-selected].day-range-end)]:bg-purple-600 [&:has([aria-selected].day-range-end)]:text-white [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-range-start)]:rounded-l-md [&:has([aria-selected].day-range-middle)]:bg-transparent focus-within:relative focus-within:z-20',
						day: 'size-8 2xl:size-10 flex items-center justify-center font-medium text-xs 3xl:text-sm',
						head_cell:
							'text-slate-500 w-full font-normal text-sm 3xl:mb-4 uppercase',
						head_row: 'flex w-full mx-auto',
						table: 'w-full',
						row: 'flex w-full justify-between gap-1 3xl:gap-2',
						months:
							'flex flex-row space-x-8 3xl:space-x-10 px-8 h-[270px] 3xl:h-full 3xl:min-h-[302px]',
						nav_button_previous: 'absolute -left-12 top-28 border-0',
						nav_button_next: 'absolute -right-12 top-28 border-0',
						caption_label: 'text-sm 3xl:text-xl',
						multiple_months: 'px-2 !p-0 3xl:!p-4 3xl:px-0',
					}}
				/>
				<div className="flex justify-between items-center">
					<Button
						variant={'ghost'}
						className="font-normal flex items-center gap-2 px-2 h-12"
						onClick={clear}
					>
						<Icon src={Icons.broom} className="!w-6 !h-6" />
						Clear All
					</Button>

					<Button
						variant={'ghost'}
						disabled={!date?.from || !date?.to}
						className=" px-6 h-8 flex rounded-[100px] items-center text-base font-semibold border text-[#8039DF] border-[#8039DF] disabled:text-[#B18EE0] disabled:border-[#B18EE0]"
						onClick={applyChanges}
					>
						Apply
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
};
